import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FiPlay,
  FiAlertCircle,
  FiExternalLink,
  FiDatabase,
  FiCpu,
  FiLayers,
  FiChevronDown,
  FiCheck,
  FiZap,
  FiX,
  FiEye,
  FiMessageSquare,
  FiFileText,
  FiHeart,
  FiBookOpen,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import {
  createRun,
  fetchQueueSessionRuns,
  cancelRun,
} from "../../services/runService";
import { supabase } from "../../services/supabase";
import {
  fetchSnapshotManifest,
  fetchSnapshot,
  flattenSnapshotConfig,
  resolveConfigForFilters,
} from "../../services/snapshotService";
import { fetchLeavesForGrade } from "../../services/curriculumApiService";
import {
  checkMaintenanceMode,
  fetchInceptBenchVersions,
  fetchCurriculumVersions,
} from "../../services/configService";
import {
  REQUEST_CATEGORY_CONFIG,
  getModifierBadges,
} from "../../utils/curriculumConfig";
import { compareGrades } from "../../utils/gradeSort";
import GeneratorDropdown from "./GeneratorDropdown";
import SubjectGradeFilter from "./SubjectGradeFilter";

// Curriculum grade-label config keyed by DB-side curriculum (lowercased).
// The display name itself comes straight from the DB string — no abbreviation
// or expansion is shown to the user.
const curriculumConfig = {
  cc: { gradeLabel: "Grade" },
  teks: { gradeLabel: "Grade" },
  ap: { gradeLabel: "Unit" },
  sat: { gradeLabel: "Unit" },
  "athena-cc": { gradeLabel: "Grade" },
};

const getCurriculumDisplayName = (dbName) => dbName || "";

const compareVersions = (a, b) => {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na < nb) {
      return -1;
    }
    if (na > nb) {
      return 1;
    }
  }
  return 0;
};

const RunPanel = ({ generators, loading }) => {
  const { user } = useAuth();

  // Generator selection
  const [selectedGenerator, setSelectedGenerator] = useState("");

  // Evaluator selection
  const [inceptbenchVersions, setInceptbenchVersions] = useState([]);
  const [selectedInceptbenchVersion, setSelectedInceptbenchVersion] =
    useState("");
  const [loadingInceptbenchVersions, setLoadingInceptbenchVersions] =
    useState(true);

  // Curriculum version selection
  const [curriculumVersions, setCurriculumVersions] = useState([]);
  const [selectedCurriculumVersion, setSelectedCurriculumVersion] =
    useState("");
  const [loadingCurriculumVersions, setLoadingCurriculumVersions] =
    useState(true);
  const [curriculumVersionDropdownOpen, setCurriculumVersionDropdownOpen] =
    useState(false);

  // Dataset version selection — driven by snapshots/manifest.json
  const [datasetVersions, setDatasetVersions] = useState([]);
  const [selectedDatasetVersion, setSelectedDatasetVersion] = useState("");
  const [loadingDatasetVersions, setLoadingDatasetVersions] = useState(true);
  const [datasetVersionDropdownOpen, setDatasetVersionDropdownOpen] =
    useState(false);

  // Benchmark selection (driven by the loaded snapshot, flattened)
  const [dimensionConfigsCache, setDimensionConfigsCache] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  const [curricula, setCurricula] = useState([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState("");

  const [subjectGradeMap, setSubjectGradeMap] = useState({});
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [loadingSubjectGrades, setLoadingSubjectGrades] = useState(false);

  // Request category and modifiers
  const [selectedRequestCategory, setSelectedRequestCategory] =
    useState("question");
  const [selectedHasInterests, setSelectedHasInterests] = useState(false);
  const [selectedHasStimulus, setSelectedHasStimulus] = useState(false);

  // Repeat multiplier — duplicates each request N times so small datasets
  // (< 100 items) have enough samples for meaningful evaluation.
  const [selectedRepeatCount, setSelectedRepeatCount] = useState(1);
  // Estimated base request count for the current selection (before repeating).
  // null = not yet computed / loading.
  const [estimatedBaseCount, setEstimatedBaseCount] = useState(null);
  // True while the async leaf-count fetch is in progress so the Run button
  // stays disabled until the repeat multiplier is resolved.
  const [isEstimating, setIsEstimating] = useState(false);
  // The loaded snapshot object, kept in state so the estimator effect can
  // react when a new dataset version is selected.
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [queueRuns, setQueueRuns] = useState([]); // All active queue runs
  // Track which runs are being cancelled. Using a Set (not a single id)
  // so a user can cancel run A and run B back-to-back without B's cancel
  // reactivating A's button mid-flight.
  const [cancellingRunIds, setCancellingRunIds] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Dropdown open states
  const [curriculumDropdownOpen, setCurriculumDropdownOpen] = useState(false);
  const [inceptbenchVersionDropdownOpen, setInceptbenchVersionDropdownOpen] =
    useState(false);

  // Curriculum version selection is only supported from InceptBench v2.3.3+
  const isCurriculumVersionDisabled =
    !selectedInceptbenchVersion ||
    compareVersions(selectedInceptbenchVersion, "2.3.3") < 0;

  // Check maintenance mode on mount
  useEffect(() => {
    const checkMaintenance = async () => {
      const { isMaintenanceMode } = await checkMaintenanceMode();
      setMaintenanceMode(isMaintenanceMode);
    };
    checkMaintenance();
  }, []);

  // Load InceptBench versions on mount
  useEffect(() => {
    const loadInceptbenchVersions = async () => {
      setLoadingInceptbenchVersions(true);
      const { data, error: fetchError } = await fetchInceptBenchVersions();

      if (data && !fetchError) {
        setInceptbenchVersions(data.versions || []);
        // Set default to current/latest version
        if (data.current) {
          setSelectedInceptbenchVersion(data.current);
        }
      }
      setLoadingInceptbenchVersions(false);
    };

    loadInceptbenchVersions();
  }, []);

  // Clear curriculum version when InceptBench version doesn't support it
  useEffect(() => {
    if (isCurriculumVersionDisabled) {
      setSelectedCurriculumVersion("");
    }
  }, [isCurriculumVersionDisabled]);

  // Load curriculum versions on mount
  useEffect(() => {
    const loadCurriculumVersions = async () => {
      setLoadingCurriculumVersions(true);
      const { data, error: fetchError } = await fetchCurriculumVersions();

      if (data && !fetchError) {
        setCurriculumVersions(data.versions || []);
        // Set default to latest version
        const latest = data.versions?.find((v) => v.isLatest);
        if (latest) {
          setSelectedCurriculumVersion(latest.version);
        } else if (data.versions?.length > 0) {
          setSelectedCurriculumVersion(data.versions[0].version);
        }
      }
      setLoadingCurriculumVersions(false);
    };

    loadCurriculumVersions();
  }, []);

  // Load snapshot manifest on mount — drives the dataset version dropdown
  useEffect(() => {
    const loadManifest = async () => {
      setLoadingDatasetVersions(true);
      const { data, error: fetchError } = await fetchSnapshotManifest();
      if (data && !fetchError && data.versions?.length > 0) {
        setDatasetVersions(data.versions);
        // Default to the manifest's `latest`, or the first version
        setSelectedDatasetVersion(data.latest || data.versions[0].version);
      }
      setLoadingDatasetVersions(false);
    };
    loadManifest();
  }, []);

  // Whenever the selected dataset version changes, load that snapshot and
  // flatten its config into the cache used to drive curriculum/subject/grade
  // dropdowns (same shape the page used to get from bm_dimension_config).
  //
  // The `cancelled` flag guards against rapid version switching: a slow
  // fetch for an older selection must not overwrite the cache once the user
  // has moved on to a newer one.
  useEffect(() => {
    if (!selectedDatasetVersion) {
      setDimensionConfigsCache([]);
      return undefined;
    }
    let cancelled = false;
    const loadSnapshot = async () => {
      setLoadingConfigs(true);
      const { data: snapshot, error: fetchError } = await fetchSnapshot(
        selectedDatasetVersion
      );
      if (cancelled) {
        return;
      }
      if (snapshot && !fetchError) {
        setDimensionConfigsCache(flattenSnapshotConfig(snapshot));
        setLoadedSnapshot(snapshot);
      } else {
        setDimensionConfigsCache([]);
        setLoadedSnapshot(null);
      }
      setLoadingConfigs(false);
    };
    loadSnapshot();
    return () => {
      cancelled = true;
    };
  }, [selectedDatasetVersion]);

  // Estimate the base request count for the current selection and auto-set
  // the repeat multiplier to the minimum needed to reach >= 200 items.
  useEffect(() => {
    if (
      !loadedSnapshot ||
      !selectedCurriculum ||
      !selectedSubjects[0] ||
      !selectedGrades[0]
    ) {
      setEstimatedBaseCount(null);
      setIsEstimating(false);
      return undefined;
    }

    setEstimatedBaseCount(null);
    setIsEstimating(true);

    let cancelled = false;

    const estimate = async () => {
      try {
        const filters = {
          request_category: selectedRequestCategory,
          has_interests: selectedHasInterests,
          has_stimulus: selectedHasStimulus,
        };
        const resolved = resolveConfigForFilters(
          loadedSnapshot,
          selectedCurriculum,
          selectedSubjects[0],
          filters
        );
        if (!resolved) {
          if (!cancelled) {
            setEstimatedBaseCount(null);
            setIsEstimating(false);
          }
          return;
        }
        const { config, activeModifiers } = resolved;

        const { data: leaves } = await fetchLeavesForGrade(
          selectedCurriculum,
          selectedSubjects[0],
          selectedGrades[0],
          selectedCurriculumVersion || undefined
        );
        if (cancelled) {
          return;
        }

        if (!leaves || leaves.length === 0) {
          setEstimatedBaseCount(0);
          setSelectedRepeatCount(1);
          setIsEstimating(false);
          return;
        }

        // Mirror the variant-pair logic from prepareRequests.js
        const configTypes =
          config.types && config.types.length > 0 ? config.types : [null];
        const configDifficulties =
          config.difficulties && config.difficulties.length > 0
            ? config.difficulties
            : [null];

        let variantCount = 0;
        for (const leaf of leaves) {
          const useLeafFormats =
            leaf.supportedFormats &&
            (!config.types || config.types.length === 0);
          if (useLeafFormats) {
            for (const [, diffs] of Object.entries(leaf.supportedFormats)) {
              variantCount += diffs && diffs.length > 0 ? diffs.length : 1;
            }
          } else {
            variantCount += configTypes.length * configDifficulties.length;
          }
        }

        const base = variantCount * activeModifiers.length;
        setEstimatedBaseCount(base);

        // Auto-select the minimum multiplier to reach >= 200 items.
        // Only applies to question runs — articles are not repeated.
        if (selectedRequestCategory === "question") {
          const needed = base > 0 ? Math.ceil(200 / base) : 1;
          setSelectedRepeatCount(needed);
        } else {
          setSelectedRepeatCount(1);
        }
        setIsEstimating(false);
      } catch {
        if (!cancelled) {
          setEstimatedBaseCount(null);
          setIsEstimating(false);
        }
      }
    };

    estimate();
    return () => {
      cancelled = true;
    };
  }, [
    loadedSnapshot,
    selectedCurriculum,
    selectedSubjects,
    selectedGrades,
    selectedRequestCategory,
    selectedHasInterests,
    selectedHasStimulus,
    selectedCurriculumVersion,
  ]);

  // Derive which (request_category, has_interests, has_stimulus) combos exist
  // in the snapshot so the UI can disable buttons that would yield no results.
  const availableCombos = useMemo(() => {
    const combos = new Set();
    const categories = new Set();
    for (const entry of dimensionConfigsCache) {
      combos.add(
        `${entry.request_category}|${entry.has_interests}|${entry.has_stimulus}`
      );
      categories.add(entry.request_category);
    }
    const hasCombo = (category, hasInterests, hasStimulus) =>
      combos.has(`${category}|${hasInterests}|${hasStimulus}`);
    return { categories, hasCombo };
  }, [dimensionConfigsCache]);

  // Filter configs by request category + modifiers, then derive curricula
  useEffect(() => {
    if (dimensionConfigsCache.length === 0) {
      setCurricula([]);
      setSelectedCurriculum("");
      setSubjectGradeMap({});
      setSelectedSubjects([]);
      setSelectedGrades([]);
      return;
    }

    // dimensionConfigsCache is a flat list of {curriculum, subject, grade,
    // request_category, has_interests, has_stimulus} from flattenSnapshotConfig.
    const filtered = dimensionConfigsCache.filter(
      (entry) =>
        entry.request_category === selectedRequestCategory &&
        entry.has_interests === selectedHasInterests &&
        entry.has_stimulus === selectedHasStimulus
    );

    const uniqueCurricula = [
      ...new Set(filtered.map((c) => c.curriculum).filter(Boolean)),
    ];
    setCurricula(uniqueCurricula);

    // Keep current curriculum if it still exists
    if (selectedCurriculum && uniqueCurricula.includes(selectedCurriculum)) {
      return;
    }

    setSelectedSubjects([]);
    setSelectedGrades([]);
    setSubjectGradeMap({});

    const commonCore = uniqueCurricula.find((c) => c.toLowerCase() === "cc");
    setSelectedCurriculum(commonCore || uniqueCurricula[0] || "");
  }, [
    dimensionConfigsCache,
    selectedRequestCategory,
    selectedHasInterests,
    selectedHasStimulus,
    selectedCurriculum,
  ]);

  // Derive subjects/grades from dimension configs filtered by category + modifiers + curriculum
  useEffect(() => {
    if (!selectedCurriculum || dimensionConfigsCache.length === 0) {
      setSubjectGradeMap({});
      setSelectedSubjects([]);
      setSelectedGrades([]);
      return;
    }

    setLoadingSubjectGrades(true);
    setSelectedSubjects([]);
    setSelectedGrades([]);

    const gradeMap = {};
    dimensionConfigsCache
      .filter(
        (entry) =>
          entry.curriculum?.toLowerCase() ===
            selectedCurriculum.toLowerCase() &&
          entry.request_category === selectedRequestCategory &&
          entry.has_interests === selectedHasInterests &&
          entry.has_stimulus === selectedHasStimulus
      )
      .forEach((entry) => {
        // Preserve the snapshot's exact subject spelling
        const subject = entry.subject;
        if (!gradeMap[subject]) {
          gradeMap[subject] = new Set();
        }
        gradeMap[subject].add(entry.grade);
      });

    const sortedMap = {};
    Object.keys(gradeMap)
      .sort()
      .forEach((subject) => {
        sortedMap[subject] = [...gradeMap[subject]].sort(compareGrades);
      });

    setSubjectGradeMap(sortedMap);
    setLoadingSubjectGrades(false);
  }, [
    selectedCurriculum,
    dimensionConfigsCache,
    selectedRequestCategory,
    selectedHasInterests,
    selectedHasStimulus,
  ]);

  // Check if selected generator still exists
  useEffect(() => {
    if (selectedGenerator && generators.length > 0) {
      const stillExists = generators.some(
        (gen) => gen.id === selectedGenerator
      );
      if (!stillExists) {
        setSelectedGenerator("");
      }
    }
  }, [generators, selectedGenerator]);

  // Under the global queue a user can have multiple runs generating/evaluating
  // concurrently (up to bm_config.max_parallel_runs across all users), so this
  // is an array, not a single run.
  const [activeRuns, setActiveRuns] = useState([]);

  const loadQueueRuns = async () => {
    if (!user) {
      return;
    }
    const { data } = await fetchQueueSessionRuns(user.id);
    if (data) {
      const notTerminal = data.filter(
        (run) => run.status !== "completed" && run.status !== "failed"
      );
      setActiveRuns(
        notTerminal.filter(
          (run) => run.status === "generating" || run.status === "evaluating"
        )
      );
      setQueueRuns(notTerminal.filter((run) => run.status === "queued"));
    }
  };

  // Load queue runs on mount and poll every 3 seconds
  useEffect(() => {
    if (!user) {
      return;
    }

    // Initial load
    loadQueueRuns();

    // Poll for updates every 3 seconds
    const interval = setInterval(() => {
      loadQueueRuns();
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Helper function to get status display info.
  // Note: under the global queue, "next to run" is determined globally by
  // order_tag across all users — we can't infer it from this user's list
  // alone, so every queued row just shows "Queued".
  const getStatusInfo = (run) => {
    switch (run.status) {
      case "queued":
        return {
          label: "Queued",
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          borderColor: "border-gray-300",
        };
      case "generating":
        return {
          label: "Running",
          bgColor: "bg-blue-100",
          textColor: "text-blue-800",
          borderColor: "border-blue-300",
        };
      case "evaluating":
        return {
          label: "Running",
          bgColor: "bg-blue-100",
          textColor: "text-blue-800",
          borderColor: "border-blue-300",
        };
      case "completed":
        return {
          label: "Completed",
          bgColor: "bg-green-100",
          textColor: "text-green-800",
          borderColor: "border-green-300",
        };
      case "failed":
        return {
          label: "Failed",
          bgColor: "bg-red-100",
          textColor: "text-red-800",
          borderColor: "border-red-300",
        };
      default:
        return {
          label: run.status,
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          borderColor: "border-gray-300",
        };
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleAddToQueue = async () => {
    // Validation
    if (!user) {
      alert("You must be logged in to run a benchmark");
      return;
    }

    if (!selectedGenerator) {
      alert("Please select a generator");
      return;
    }

    if (!selectedInceptbenchVersion) {
      alert("Please select an InceptBench version");
      return;
    }

    if (!selectedCurriculum) {
      alert("Please select a curriculum");
      return;
    }

    if (!selectedDatasetVersion) {
      alert("Please select a dataset version");
      return;
    }

    if (selectedSubjects.length === 0) {
      alert("Please select at least one subject");
      return;
    }

    if (selectedGrades.length === 0) {
      alert("Please select a grade");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Re-check maintenance mode
      const { isMaintenanceMode } = await checkMaintenanceMode();
      if (isMaintenanceMode) {
        setMaintenanceMode(true);
        setError("Benchmark runs are currently paused for maintenance.");
        setSubmitting(false);
        return;
      }

      // Create run directly in database
      const { error: createError } = await createRun(
        selectedGenerator,
        {
          // Preserve the snapshot's exact subject spelling so downstream
          // lookups (worker prepareRequests, snapshot resolveConfig) match.
          subjects: selectedSubjects,
          grades: selectedGrades,
          curriculum: selectedCurriculum,
          request_category: selectedRequestCategory,
          has_interests: selectedHasInterests,
          has_stimulus: selectedHasStimulus,
          repeat_count:
            selectedRepeatCount > 1 ? selectedRepeatCount : undefined,
        },
        user.id,
        selectedDatasetVersion, // snapshot version label, e.g. "2.0.0"
        selectedInceptbenchVersion,
        selectedCurriculumVersion || null
        // sourceRunId + configVersionId default to null. configVersionId is a
        // deprecated FK retained only for backward compatibility — snapshot
        // label in dataset_version is the source of truth.
      );

      if (createError) {
        throw new Error(`Failed to create run: ${createError.message}`);
      }

      // Refresh queue display
      await loadQueueRuns();

      // Poke the global queue — it will start this run immediately if a slot
      // is free, otherwise the cron safety-net will pick it up within 30s.
      // (Pre-global-queue this was gated on "first in user's queue"; that
      // gate is obsolete now that the scheduler is global.)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        const supabaseUrl = supabase.supabaseUrl;
        await fetch(`${supabaseUrl}/functions/v1/process-queue`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        });
      }

      setError(null);
    } catch (err) {
      console.error("Error adding to queue:", err);
      setError(err.message || "Failed to add run to queue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRun = async (runId) => {
    if (!window.confirm("Are you sure you want to cancel this run?")) {
      return;
    }

    setCancellingRunIds((prev) => {
      const next = new Set(prev);
      next.add(runId);
      return next;
    });
    setError(null);

    try {
      const result = await cancelRun(runId);

      if (result.success) {
        // Refresh queue to remove cancelled run
        await loadQueueRuns();

        // Trigger queue processor to start next run
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          const supabaseUrl = supabase.supabaseUrl;
          await fetch(`${supabaseUrl}/functions/v1/process-queue`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          });
        }
      } else {
        setError(result.error?.message || "Failed to cancel run");
      }
    } catch (err) {
      console.error("Error cancelling run:", err);
      setError(err.message || "Failed to cancel run");
    } finally {
      setCancellingRunIds((prev) => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
    }
  };

  const isLoading =
    loading ||
    loadingConfigs ||
    loadingInceptbenchVersions ||
    loadingCurriculumVersions;

  // Check if all required fields are selected
  const canRun =
    selectedGenerator &&
    selectedInceptbenchVersion &&
    selectedDatasetVersion &&
    selectedCurriculum &&
    selectedSubjects.length > 0 &&
    selectedGrades.length > 0 &&
    !maintenanceMode;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-soft">
      <div className="border-b border-gray-100 p-8">
        <h2 className="text-2xl font-semibold text-gray-900">Run Benchmark</h2>
        <p className="mt-2 text-sm text-gray-500">
          Configure your generator, evaluator, and benchmark settings
        </p>
      </div>

      <div className="p-8">
        {/* Loading State */}
        {isLoading ? (
          <div className="py-12 text-center">
            <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-500">
              Loading configuration options...
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* ============================================ */}
            {/* GENERATOR (full width) */}
            {/* ============================================ */}
            <fieldset className="relative rounded-xl border-2 border-gray-200 p-5 pt-4">
              <legend className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-700">
                <div className="flex size-6 items-center justify-center rounded-md bg-blue-100">
                  <FiCpu className="size-3.5 text-blue-600" />
                </div>
                Generator
              </legend>

              <GeneratorDropdown
                generators={generators}
                selectedGenerator={selectedGenerator}
                onChange={setSelectedGenerator}
                disabled={submitting}
              />

              {generators.length === 0 && (
                <div className="mt-3 flex items-center rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <FiAlertCircle className="mr-2 size-4 shrink-0" />
                  No generators available. Please add a generator first.
                </div>
              )}
            </fieldset>

            {/* ============================================ */}
            {/* EVALUATOR (full width, 2 dropdowns) */}
            {/* ============================================ */}
            <fieldset className="relative rounded-xl border-2 border-gray-200 p-5 pt-4">
              <legend className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-700">
                <div className="flex size-6 items-center justify-center rounded-md bg-purple-100">
                  <FiZap className="size-3.5 text-purple-600" />
                </div>
                Evaluator
              </legend>

              <div className="grid gap-4 md:grid-cols-2">
                {/* InceptBench Version Dropdown */}
                <div className="relative">
                  <div className="mb-2 flex items-center justify-between">
                    <label
                      htmlFor="inceptbench-version-dropdown"
                      className="block text-sm font-medium text-gray-700"
                    >
                      InceptBench Version *
                    </label>
                    <a
                      href="https://www.inceptbench.com/release-notes/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs font-medium text-purple-600 transition-colors hover:text-purple-800"
                    >
                      <FiZap className="size-4" />
                      See what&apos;s changed in the latest release
                    </a>
                  </div>
                  <button
                    id="inceptbench-version-dropdown"
                    type="button"
                    onClick={() =>
                      setInceptbenchVersionDropdownOpen(
                        !inceptbenchVersionDropdownOpen
                      )
                    }
                    disabled={submitting}
                    className="flex w-full items-center justify-between rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-left transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <FiZap className="size-5 text-purple-500" />
                      <div>
                        {selectedInceptbenchVersion ? (
                          <p className="flex items-center gap-2 font-medium text-gray-900">
                            v{selectedInceptbenchVersion}
                            {inceptbenchVersions.find(
                              (v) => v.version === selectedInceptbenchVersion
                            )?.isLatest && (
                              <span className="text-xs text-gray-500">
                                Latest
                              </span>
                            )}
                            {selectedInceptbenchVersion === "2.3.5" && (
                              <span className="relative inline-flex items-center">
                                <span className="rounded-sm bg-amber-400 px-1 py-px text-[9px] font-bold leading-none tracking-wide text-white">
                                  EXPERIMENTAL
                                </span>
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-gray-400">Select version...</p>
                        )}
                      </div>
                    </div>
                    <FiChevronDown
                      className={`size-5 text-gray-400 transition-transform ${inceptbenchVersionDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {inceptbenchVersionDropdownOpen && (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-10 cursor-default"
                        onClick={() => setInceptbenchVersionDropdownOpen(false)}
                        aria-label="Close dropdown"
                      />
                      <div className="absolute inset-x-0 z-20 mt-2 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        {inceptbenchVersions.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No versions available
                          </div>
                        ) : (
                          inceptbenchVersions.map((v) => (
                            <button
                              key={v.version}
                              type="button"
                              onClick={() => {
                                setSelectedInceptbenchVersion(v.version);
                                setInceptbenchVersionDropdownOpen(false);
                              }}
                              className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                                selectedInceptbenchVersion === v.version
                                  ? "bg-primary-50"
                                  : ""
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="relative inline-flex items-center">
                                  <span
                                    className={`font-medium ${selectedInceptbenchVersion === v.version ? "text-primary-700" : "text-gray-900"}`}
                                  >
                                    v{v.version}
                                  </span>
                                  {v.version === "2.3.5" && (
                                    <span className="absolute -right-5 -top-2 rounded-sm bg-amber-400 px-1 py-px text-[9px] font-bold leading-none tracking-wide text-white">
                                      EXP
                                    </span>
                                  )}
                                </span>
                                {v.isLatest && (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                    Latest
                                  </span>
                                )}
                              </div>
                              {selectedInceptbenchVersion === v.version && (
                                <FiCheck className="size-4 text-primary-600" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Curriculum Version Dropdown */}
                <div className="relative">
                  <label
                    htmlFor="curriculum-version-dropdown"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Curriculum Version *
                  </label>
                  <button
                    id="curriculum-version-dropdown"
                    type="button"
                    onClick={() =>
                      setCurriculumVersionDropdownOpen(
                        !curriculumVersionDropdownOpen
                      )
                    }
                    disabled={submitting || isCurriculumVersionDisabled}
                    className="flex w-full items-center justify-between rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-left transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <FiLayers className="size-5 text-amber-500" />
                      <div>
                        {selectedCurriculumVersion ? (
                          <p className="font-medium text-gray-900">
                            v{selectedCurriculumVersion}
                            {curriculumVersions.find(
                              (v) => v.version === selectedCurriculumVersion
                            )?.isLatest && (
                              <span className="ml-2 text-xs text-gray-500">
                                Latest
                              </span>
                            )}
                            {curriculumVersions.find(
                              (v) => v.version === selectedCurriculumVersion
                            )?.versionDate && (
                              <span className="ml-2 text-xs text-gray-500">
                                (
                                {formatDate(
                                  curriculumVersions.find(
                                    (v) =>
                                      v.version === selectedCurriculumVersion
                                  ).versionDate
                                )}
                                )
                              </span>
                            )}
                          </p>
                        ) : isCurriculumVersionDisabled ? (
                          <p className="font-medium text-gray-900">Latest</p>
                        ) : (
                          <p className="text-gray-400">Select version...</p>
                        )}
                      </div>
                    </div>
                    <FiChevronDown
                      className={`size-5 text-gray-400 transition-transform ${curriculumVersionDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isCurriculumVersionDisabled && !submitting && (
                    <p className="mt-1 text-xs text-amber-600">
                      Curriculum Version selection is only supported from
                      InceptBench v2.3.3 onwards.
                    </p>
                  )}

                  {curriculumVersionDropdownOpen && (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-10 cursor-default"
                        onClick={() => setCurriculumVersionDropdownOpen(false)}
                        aria-label="Close dropdown"
                      />
                      <div className="absolute inset-x-0 z-20 mt-2 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        {curriculumVersions.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No versions available
                          </div>
                        ) : (
                          curriculumVersions.map((v) => (
                            <button
                              key={v.version}
                              type="button"
                              onClick={() => {
                                setSelectedCurriculumVersion(v.version);
                                setCurriculumVersionDropdownOpen(false);
                              }}
                              className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                                selectedCurriculumVersion === v.version
                                  ? "bg-primary-50"
                                  : ""
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className={`font-medium ${selectedCurriculumVersion === v.version ? "text-primary-700" : "text-gray-900"}`}
                                >
                                  v{v.version}
                                </span>
                                {v.isLatest && (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                    Latest
                                  </span>
                                )}
                                {v.versionDate && (
                                  <span className="text-sm text-gray-500">
                                    ({formatDate(v.versionDate)})
                                  </span>
                                )}
                              </div>
                              {selectedCurriculumVersion === v.version && (
                                <FiCheck className="size-4 text-primary-600" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </fieldset>

            {/* ============================================ */}
            {/* SECTION 2: BENCHMARK - Fieldset style */}
            {/* ============================================ */}
            <fieldset className="relative rounded-xl border-2 border-gray-200 p-5 pt-4">
              <legend className="flex items-center justify-between px-2 text-sm font-semibold text-gray-700">
                <div className="flex items-center gap-2">
                  <div className="flex size-6 items-center justify-center rounded-md bg-emerald-100">
                    <FiLayers className="size-3.5 text-emerald-600" />
                  </div>
                  Benchmark
                </div>
              </legend>
              <div className="mb-2 flex justify-end">
                <Link
                  to="/benchmark-sets"
                  className="flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  <FiExternalLink className="size-3.5" />
                  <span>View Benchmark Sets</span>
                </Link>
              </div>

              {/* Row 0: Request Type & Modifiers */}
              <div className="mb-6 grid gap-4 md:grid-cols-2">
                {/* Request Type */}
                <div>
                  <span className="mb-2 block text-sm font-medium text-gray-700">
                    Request Type *
                  </span>
                  <div className="flex gap-2">
                    {Object.entries(REQUEST_CATEGORY_CONFIG).map(
                      ([key, config]) => {
                        const isSelected = selectedRequestCategory === key;
                        const Icon =
                          key === "question" ? FiMessageSquare : FiFileText;
                        const isAvailable =
                          dimensionConfigsCache.length === 0 ||
                          availableCombos.categories.has(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            title={
                              !isAvailable
                                ? "No configurations exist in this dataset for this request type"
                                : undefined
                            }
                            onClick={() => {
                              setSelectedRequestCategory(key);
                              setSelectedHasInterests(false);
                              setSelectedHasStimulus(false);
                            }}
                            disabled={submitting || !isAvailable}
                            className={`flex flex-1 items-center justify-between rounded-lg border-2 px-4 py-3 transition-colors ${
                              isSelected
                                ? `${config.borderColor} ${config.bgColor}`
                                : "border-gray-300 bg-white hover:border-gray-400"
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            <div className="flex items-center gap-3">
                              <Icon
                                className={`size-5 ${key === "question" ? "text-blue-500" : "text-emerald-500"}`}
                              />
                              <span
                                className={`font-medium ${isSelected ? config.color : "text-gray-900"}`}
                              >
                                {config.label}
                              </span>
                            </div>
                            {isSelected && (
                              <FiCheck
                                className={`size-4 shrink-0 ${config.color}`}
                              />
                            )}
                          </button>
                        );
                      }
                    )}
                  </div>
                </div>

                {/* Modifiers */}
                <div>
                  <span className="mb-2 block text-sm font-medium text-gray-700">
                    Modifiers
                  </span>
                  <div className="flex gap-2">
                    {(() => {
                      // A modifier flag is available iff the snapshot has at
                      // least one combo with the currently selected category +
                      // that flag set true (and the other flag false).
                      const interestsAvailable =
                        dimensionConfigsCache.length === 0 ||
                        availableCombos.hasCombo(
                          selectedRequestCategory,
                          true,
                          false
                        );
                      const stimulusAvailable =
                        dimensionConfigsCache.length === 0 ||
                        availableCombos.hasCombo(
                          selectedRequestCategory,
                          false,
                          true
                        );
                      return (
                        <>
                          {/* Interests */}
                          <button
                            type="button"
                            title={
                              !interestsAvailable
                                ? "No configurations exist in this dataset for this combination"
                                : undefined
                            }
                            onClick={() => {
                              const next = !selectedHasInterests;
                              setSelectedHasInterests(next);
                              if (next) {
                                setSelectedHasStimulus(false);
                              }
                            }}
                            disabled={submitting || !interestsAvailable}
                            className={`flex flex-1 items-center justify-between rounded-lg border-2 px-4 py-3 transition-colors ${
                              selectedHasInterests
                                ? "border-amber-300 bg-amber-50"
                                : "border-gray-300 bg-white hover:border-gray-400"
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            <div className="flex items-center gap-3">
                              <FiHeart className="size-5 text-amber-500" />
                              <span
                                className={`font-medium ${selectedHasInterests ? "text-amber-700" : "text-gray-900"}`}
                              >
                                Interests
                              </span>
                            </div>
                            {selectedHasInterests && (
                              <FiCheck className="size-4 shrink-0 text-amber-600" />
                            )}
                          </button>

                          {/* Stimulus */}
                          <button
                            type="button"
                            title={
                              !stimulusAvailable
                                ? "No configurations exist in this dataset for this combination"
                                : undefined
                            }
                            onClick={() => {
                              const next = !selectedHasStimulus;
                              setSelectedHasStimulus(next);
                              if (next) {
                                setSelectedHasInterests(false);
                              }
                            }}
                            disabled={submitting || !stimulusAvailable}
                            className={`flex flex-1 items-center justify-between rounded-lg border-2 px-4 py-3 transition-colors ${
                              selectedHasStimulus
                                ? "border-rose-300 bg-rose-50"
                                : "border-gray-300 bg-white hover:border-gray-400"
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            <div className="flex items-center gap-3">
                              <FiBookOpen className="size-5 text-rose-500" />
                              <span
                                className={`font-medium ${selectedHasStimulus ? "text-rose-700" : "text-gray-900"}`}
                              >
                                Stimulus
                              </span>
                            </div>
                            {selectedHasStimulus && (
                              <FiCheck className="size-4 shrink-0 text-rose-600" />
                            )}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Dataset Version & Curriculum */}
              <div className="mb-6 grid gap-4 md:grid-cols-2">
                <div className="relative">
                  <label
                    htmlFor="dataset-version-dropdown"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Dataset Version
                  </label>
                  <button
                    id="dataset-version-dropdown"
                    type="button"
                    onClick={() =>
                      setDatasetVersionDropdownOpen(!datasetVersionDropdownOpen)
                    }
                    disabled={submitting || loadingDatasetVersions}
                    className="flex w-full items-center justify-between rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-left transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <FiLayers className="size-5 text-violet-500" />
                      <div>
                        {loadingDatasetVersions ? (
                          <p className="text-gray-400">Loading...</p>
                        ) : selectedDatasetVersion ? (
                          <p className="font-medium text-gray-900">
                            v{selectedDatasetVersion}
                            {datasetVersions[0]?.version ===
                              selectedDatasetVersion && (
                              <span className="ml-2 text-xs text-gray-500">
                                Latest
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-gray-400">
                            Select dataset version...
                          </p>
                        )}
                      </div>
                    </div>
                    <FiChevronDown
                      className={`size-5 text-gray-400 transition-transform ${datasetVersionDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {datasetVersionDropdownOpen && (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-10 cursor-default"
                        onClick={() => setDatasetVersionDropdownOpen(false)}
                        aria-label="Close dropdown"
                      />
                      <div className="absolute inset-x-0 z-20 mt-2 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        {datasetVersions.map((v, idx) => (
                          <button
                            key={v.version}
                            type="button"
                            onClick={() => {
                              setSelectedDatasetVersion(v.version);
                              setDatasetVersionDropdownOpen(false);
                            }}
                            className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                              selectedDatasetVersion === v.version
                                ? "bg-primary-50"
                                : ""
                            }`}
                          >
                            <div>
                              <span
                                className={`font-medium ${selectedDatasetVersion === v.version ? "text-primary-700" : "text-gray-900"}`}
                              >
                                v{v.version}
                              </span>
                              {idx === 0 && (
                                <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                  Latest
                                </span>
                              )}
                              {v.description && (
                                <span className="ml-2 text-sm text-gray-500">
                                  ({v.description})
                                </span>
                              )}
                            </div>
                            {selectedDatasetVersion === v.version && (
                              <FiCheck className="size-4 text-primary-600" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Curriculum Dropdown */}
                <div className="relative">
                  <label
                    htmlFor="curriculum-dropdown"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Curriculum *
                  </label>
                  <button
                    id="curriculum-dropdown"
                    type="button"
                    onClick={() =>
                      setCurriculumDropdownOpen(!curriculumDropdownOpen)
                    }
                    disabled={submitting || loadingConfigs}
                    className="flex w-full items-center justify-between rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-left transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <FiLayers className="size-5 text-sky-500" />
                      <div>
                        {loadingConfigs ? (
                          <p className="text-gray-400">Loading...</p>
                        ) : selectedCurriculum ? (
                          <p className="font-medium text-gray-900">
                            {getCurriculumDisplayName(selectedCurriculum)}
                          </p>
                        ) : (
                          <p className="text-gray-400">Select curriculum...</p>
                        )}
                      </div>
                    </div>
                    <FiChevronDown
                      className={`size-5 text-gray-400 transition-transform ${curriculumDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {curriculumDropdownOpen && curricula.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-10 cursor-default"
                        onClick={() => setCurriculumDropdownOpen(false)}
                        aria-label="Close dropdown"
                      />
                      <div className="absolute inset-x-0 z-20 mt-2 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        {curricula.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => {
                              setSelectedCurriculum(c);
                              setCurriculumDropdownOpen(false);
                            }}
                            className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                              selectedCurriculum === c ? "bg-primary-50" : ""
                            }`}
                          >
                            <span
                              className={`font-medium ${selectedCurriculum === c ? "text-primary-700" : "text-gray-900"}`}
                            >
                              {getCurriculumDisplayName(c)}
                            </span>
                            {selectedCurriculum === c && (
                              <FiCheck className="size-4 text-primary-600" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Row 2: Subject & Grade */}
              {loadingSubjectGrades ? (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block size-6 animate-spin rounded-full border-2 border-solid border-primary-600 border-r-transparent"></div>
                  <span className="ml-3 text-sm text-gray-500">
                    Loading subjects and grades...
                  </span>
                </div>
              ) : selectedCurriculum &&
                Object.keys(subjectGradeMap).length > 0 ? (
                <SubjectGradeFilter
                  subjectGradeMap={subjectGradeMap}
                  selectedSubjects={selectedSubjects}
                  selectedGrades={selectedGrades}
                  onSubjectsChange={setSelectedSubjects}
                  onGradesChange={setSelectedGrades}
                  disabled={submitting}
                  curriculum={selectedCurriculum}
                />
              ) : selectedCurriculum ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No subjects/grades available for this curriculum and version
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  Select a dataset version and curriculum to see available
                  subjects and grades
                </div>
              )}

              {/* Repeat Multiplier — info only, shown for question runs under 200 */}
              {estimatedBaseCount !== null &&
                selectedRepeatCount > 1 &&
                selectedRequestCategory === "question" && (
                  <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <FiAlertCircle className="size-4 shrink-0 text-amber-500" />
                    <span>
                      Dataset has ~{estimatedBaseCount} items — each request
                      will be run{" "}
                      <span className="font-semibold">
                        {selectedRepeatCount}×
                      </span>{" "}
                      to reach ~{estimatedBaseCount * selectedRepeatCount} total
                      samples.
                    </span>
                  </div>
                )}

              {/* Selection Summary */}
              {selectedCurriculum &&
                selectedSubjects.length > 0 &&
                selectedGrades.length > 0 && (
                  <div className="mt-4 flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FiDatabase className="size-4 text-primary-600" />
                      <span className="text-sm font-medium text-primary-900">
                        Ready to run
                      </span>
                    </div>
                    <span className="text-xs text-primary-700">
                      {selectedSubjects[0]} •{" "}
                      {curriculumConfig[selectedCurriculum?.toLowerCase()]
                        ?.gradeLabel || "Grade"}{" "}
                      {selectedGrades[0]} •{" "}
                      {getCurriculumDisplayName(selectedCurriculum)}
                      {selectedRepeatCount > 1 && (
                        <span className="ml-1.5 rounded-full bg-primary-200 px-2 py-0.5 text-xs font-semibold text-primary-800">
                          {selectedRepeatCount}× items
                        </span>
                      )}
                    </span>
                  </div>
                )}
            </fieldset>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-6 flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
            <FiAlertCircle className="size-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Add to Queue Button */}
        {!isLoading && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleAddToQueue}
              disabled={submitting || !canRun || !user || isEstimating}
              title={
                maintenanceMode
                  ? "Benchmarks are under maintenance"
                  : !user
                    ? "Please log in to run benchmarks"
                    : !selectedGenerator
                      ? "Please select a generator"
                      : !selectedInceptbenchVersion
                        ? "Please select an InceptBench version"
                        : !selectedDatasetVersion
                          ? "Please select a dataset version"
                          : !selectedCurriculum
                            ? "Please select a curriculum"
                            : selectedSubjects.length === 0
                              ? "Please select a subject"
                              : selectedGrades.length === 0
                                ? "Please select a grade"
                                : isEstimating
                                  ? "Calculating repeat count…"
                                  : "Add this configuration to the queue"
              }
              className="flex items-center justify-center space-x-2 rounded-lg bg-primary-600 px-8 py-4 text-base font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-md"
            >
              <FiPlay className="size-5" />
              <span>
                {submitting
                  ? "Adding..."
                  : isEstimating
                    ? "Calculating…"
                    : "Run Benchmark"}
              </span>
            </button>
          </div>
        )}

        {/* Currently Running Cards (one per active run — the global queue can
            have multiple of this user's runs generating/evaluating at once). */}
        {activeRuns.length > 0 && (
          <div className="mt-8">
            <fieldset className="relative rounded-xl border-2 border-gray-200 p-6 pt-5">
              <legend className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-700">
                <div className="flex size-6 items-center justify-center rounded-md bg-blue-100">
                  <FiZap className="size-3.5 text-blue-600" />
                </div>
                Currently Running
                {activeRuns.length > 1 && (
                  <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                    {activeRuns.length}
                  </span>
                )}
              </legend>

              <div className="space-y-5">
                {activeRuns.map((run) => (
                  <div key={run.id} className="space-y-3">
                    {/* Row 1: Generator and Status */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
                      <div className="flex items-center justify-between">
                        <div className="text-base font-semibold text-gray-900">
                          {run.bm_generators?.name || "Unknown Generator"}
                        </div>
                        <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-blue-300 bg-white px-3 py-1.5 text-sm font-semibold text-blue-700 shadow-sm">
                          <div className="size-2 animate-pulse rounded-full bg-blue-600"></div>
                          {run.status === "generating"
                            ? "Generating"
                            : "Evaluating"}
                        </span>
                      </div>
                    </div>

                    {/* Row 2: Filters and Actions */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-gray-600">
                            Filters:
                          </span>
                          {run.filters?.request_category &&
                            run.filters.request_category !== "question" && (
                              <span
                                className={`inline-flex items-center rounded-lg px-3 py-1 text-sm font-medium ${REQUEST_CATEGORY_CONFIG[run.filters.request_category]?.bgColor || "bg-gray-100"} ${REQUEST_CATEGORY_CONFIG[run.filters.request_category]?.color || "text-gray-700"}`}
                              >
                                {REQUEST_CATEGORY_CONFIG[
                                  run.filters.request_category
                                ]?.label || run.filters.request_category}
                              </span>
                            )}
                          {getModifierBadges(run.filters).map((badge) => (
                            <span
                              key={badge.key}
                              className={`inline-flex items-center rounded-lg px-3 py-1 text-sm font-medium ${badge.bgColor} ${badge.color}`}
                            >
                              {badge.label}
                            </span>
                          ))}
                          {run.filters?.subjects?.map((subject, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-lg bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700"
                            >
                              {subject}
                            </span>
                          ))}
                          {run.filters?.grades?.map((grade, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-lg bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700"
                            >
                              {curriculumConfig[
                                run.filters?.curriculum?.toLowerCase()
                              ]?.gradeLabel || "Grade"}{" "}
                              {grade}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCancelRun(run.id)}
                            disabled={cancellingRunIds.has(run.id)}
                            className="flex items-center gap-1.5 rounded-lg border-2 border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 shadow-sm transition-all hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <FiX className="size-4" />
                            {cancellingRunIds.has(run.id)
                              ? "Cancelling..."
                              : "Cancel"}
                          </button>
                          <Link
                            to={`/past-runs/${run.id}`}
                            className="flex items-center gap-1.5 rounded-lg border-2 border-primary-200 bg-white px-3 py-1.5 text-sm font-semibold text-primary-700 shadow-sm transition-all hover:border-primary-300 hover:bg-primary-50"
                          >
                            <FiEye className="size-4" />
                            View
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">
                            Generation
                          </span>
                          <span className="text-lg font-bold text-blue-600">
                            {run.generation_progress || 0}%
                          </span>
                        </div>
                        <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 shadow-sm transition-all duration-500"
                            style={{
                              width: `${run.generation_progress || 0}%`,
                            }}
                          ></div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">
                            Evaluation
                          </span>
                          <span className="text-lg font-bold text-green-600">
                            {run.evaluation_progress || 0}%
                          </span>
                        </div>
                        <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-green-500 to-green-600 shadow-sm transition-all duration-500"
                            style={{
                              width: `${run.evaluation_progress || 0}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>
        )}

        {/* Queue Display */}
        {queueRuns.length > 0 && (
          <div className="mt-8">
            <fieldset className="relative rounded-xl border-2 border-gray-200 p-6 pt-5">
              <legend className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-700">
                <div className="flex size-6 items-center justify-center rounded-md bg-indigo-100">
                  <FiLayers className="size-3.5 text-indigo-600" />
                </div>
                Benchmark Queue
                <span className="ml-1 text-xs font-normal text-gray-500">
                  ({queueRuns.length} run{queueRuns.length !== 1 ? "s" : ""})
                </span>
              </legend>

              {/* Queue Items */}
              <div className="space-y-3">
                <div className="grid grid-cols-10 gap-4 px-4 pb-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  <div className="col-span-1">#</div>
                  <div className="col-span-2">Generator</div>
                  <div className="col-span-3">Filters</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Actions</div>
                </div>

                {/* Queue Items */}
                {queueRuns.map((run, index) => {
                  const statusInfo = getStatusInfo(run);

                  return (
                    <div
                      key={run.id}
                      className="grid grid-cols-10 items-center gap-4 rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-center transition-all hover:border-gray-300 hover:shadow-sm"
                    >
                      <div className="col-span-1 flex justify-center">
                        <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                          {index + 1}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="font-medium text-gray-900">
                          {run.bm_generators?.name || "Unknown Generator"}
                        </div>
                      </div>
                      <div className="col-span-3">
                        <div className="flex flex-wrap justify-center gap-1.5">
                          {run.filters?.request_category &&
                            run.filters.request_category !== "question" && (
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${REQUEST_CATEGORY_CONFIG[run.filters.request_category]?.bgColor || "bg-gray-100"} ${REQUEST_CATEGORY_CONFIG[run.filters.request_category]?.color || "text-gray-700"}`}
                              >
                                {REQUEST_CATEGORY_CONFIG[
                                  run.filters.request_category
                                ]?.label || run.filters.request_category}
                              </span>
                            )}
                          {getModifierBadges(run.filters).map((badge) => (
                            <span
                              key={badge.key}
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${badge.bgColor} ${badge.color}`}
                            >
                              {badge.label}
                            </span>
                          ))}
                          {run.filters?.subjects?.map((subject, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                            >
                              {subject}
                            </span>
                          ))}
                          {run.filters?.grades?.map((grade, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-md bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700"
                            >
                              {curriculumConfig[
                                run.filters?.curriculum?.toLowerCase()
                              ]?.gradeLabel || "Grade"}{" "}
                              {grade}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <span
                          className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.textColor} ${statusInfo.borderColor}`}
                        >
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleCancelRun(run.id)}
                            disabled={cancellingRunIds.has(run.id)}
                            className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Cancel this run"
                          >
                            <FiX className="size-3" />
                            {cancellingRunIds.has(run.id)
                              ? "Cancelling..."
                              : "Cancel"}
                          </button>
                          <Link
                            to={`/past-runs/${run.id}`}
                            className="flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-100"
                            title="View run details"
                          >
                            <FiEye className="size-3" />
                            View
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          </div>
        )}
      </div>
    </div>
  );
};

RunPanel.propTypes = {
  generators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      endpoint_url: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default RunPanel;
